import { Col, Row, Space, Typography, Modal, Button, Dropdown, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import Loader from "../controls/Loader";
import { SearchQuery } from "../code/search-query/search-query";
import VariablesView from "../components/VariablesView";
import { getVariablesExport } from "../repository/data.repository";
import { Variable } from "../repository/data.default";
import VariablesFormDialog from "../components/VariablesFormDialog";
import { VariableForm } from "../components/VariablesFormDialog.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function VariablesPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [variablesInputMode, setVariablesInputMode] = useState<
    "add" | "update" | "delete"
  >("update");
  const [variablesInputOpen, setVariablesInputOpen] = useState(false);
  const [variablesInputValues, setVariablesInputValues] =
    useState<VariableForm>({} as any);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onVariablesInputOpen(
    mode: "add" | "update" | "delete",
    values?: Variable,
  ): void {
    setVariablesInputMode(mode);
    setVariablesInputOpen(true);
    setVariablesInputValues(
      values
        ? {
            uid: values.uid,
            rowVersion: values?.rowVersion,
            name: values.name,
            tag: values.tag,
            description: values.description,
            value: values.value,
            contentType: {
              value: values.contentType,
              display: values.contentType,
            },
            variableType: values.variableType,
          }
        : ({} as any),
    );
  }

  function onVariablesInputFinish(): void {
    setVariablesInputOpen(false);
    setVariablesInputValues({} as any);
    setTriggerView(Guid.create().toString());
  }

  function onVariablesInputClose(): void {
    setVariablesInputOpen(false);
    setVariablesInputValues({} as any);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading variables export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getVariablesExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Variables
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("TemplatesVariables", "create")}

            className={`${!permissions.can("TemplatesVariables", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("TemplatesVariables", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onVariablesInputOpen("add")}
          >
            Add Variable
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <VariablesView
          trigger={triggerView}
          onSearch={onSearch}
          onAction={(key: string, record: any) =>
            onVariablesInputOpen(key as any, record)
          }
        />
      </Col>
      <VariablesFormDialog
        key={variablesInputMode}
        mode={variablesInputMode}
        open={variablesInputOpen}
        initialValues={variablesInputValues}
        onFinish={onVariablesInputFinish}
        onCancel={onVariablesInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default VariablesPage;
